import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, fieldIssues } from '../../api';
import { can, fieldVisible, type Me } from '../../auth/me';
import { useMe } from '../../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../../catalog/money';
import { EVENTS_RESOURCE, formatEventRange, formatSegments } from '../../events/labels';
import { ConflictNotice } from '../../ui/ConflictNotice';
import { DialogLayer } from '../../ui/Dialog';
import { NotesInput } from '../../ui/NotesInput';
import { formatDecimal, parseScaled } from '../../ui/numbers';
import { downloadFrom, offerPdfUrl, PdfPreviewDialog } from '../PdfPreviewDialog';
import { quoteFromListUrl } from '../QuotesPage';
import { LINES_RESOURCE, QUOTES_RESOURCE, TOTALS_RESOURCE } from '../types';
import { OfferEventsDialog } from './OfferEventsDialog';
import { dateStamp, discountFromInput, discountNote, discountToInput, totalsRows } from './offers';
import { DETAIL_LABELS, OFFER_STATE_LABELS, OFFERS_KEY, type DetailLevel, type OfferDiscount, type OfferEvent, type OfferOf, type OfferVersion } from './types';
import './offers.css';

/**
 * „Oferta łączona” (owner 2026-10-30 22:26): several events of one client in one offer — each event with its dates,
 * name (a link to its quote), place and total; the lines, the sections' sums or only the totals, chosen per offer; at
 * the end the sums of equipment and people, their two separate discounts, net, VAT and gross.
 */

export const offerKey = (id: string) => [...OFFERS_KEY, id] as const;
const DETAILS: DetailLevel[] = ['lines', 'sections', 'totals'];

/** `pdf`: the PDF shows every amount, so it needs every price field (as the quote's PDF). */
export type OfferRights = { edit: boolean; send: boolean; decide: boolean; seesEvents: boolean; pdf: boolean };

/** Every price field of the lines and every total (the server refuses the PDF otherwise, `price_fields_hidden`). */
const seesAllPrices = (me: Me) => ['unitPriceGrosze', 'discountBp', 'netGrosze'].every((f) => fieldVisible(me, LINES_RESOURCE, f)) && ['totals', 'discountBp'].every((f) => fieldVisible(me, TOTALS_RESOURCE, f));

export function OfferPage() {
  const { id = '' } = useParams();
  const { me } = useMe();
  const queryClient = useQueryClient();
  const data = useQuery({ queryKey: offerKey(id), queryFn: () => api<OfferOf>(`/api/quotes/offers/${id}`), retry: false });
  if (!me) return null;
  const rights: OfferRights = { edit: can(me, QUOTES_RESOURCE, 'edit'), send: can(me, QUOTES_RESOURCE, 'send'), decide: can(me, QUOTES_RESOURCE, 'decide'), seesEvents: can(me, EVENTS_RESOURCE, 'view'), pdf: seesAllPrices(me) };
  const set = (of: OfferOf) => {
    queryClient.setQueryData(offerKey(id), of);
    void queryClient.invalidateQueries({ queryKey: OFFERS_KEY, exact: true });
  };
  return (
    <section className="page wide offer-page" data-feature-id="quotes.offers">
      <p className="offer-back">
        <Link to="/wyceny?oferty=1">Wszystkie oferty łączone</Link>
      </p>
      {data.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {data.error && <p className="alert" role="alert">{data.error instanceof ApiError ? data.error.message : 'Nie udało się wczytać oferty'}</p>}
      {data.data && <OfferSheet of={data.data} rights={rights} onChanged={set} onReload={() => void data.refetch()} />}
    </section>
  );
}

/** The offer on screen (all its actions); `onChanged` gets the offer after every saved change. */
export function OfferSheet({ of, rights, onChanged: changed, onReload }: { of: OfferOf; rights: OfferRights; onChanged: (of: OfferOf) => void; onReload: () => void }) {
  // review M1: any version can be opened (an older sent one accepted); null = the current one
  const [shownId, setShownId] = useState<string | null>(null);
  const older = useQuery({
    queryKey: [...OFFERS_KEY, 'version', shownId],
    enabled: shownId !== null && shownId !== of.current.id,
    queryFn: () => api<OfferVersion>(`/api/quotes/offer-versions/${shownId}`),
  });
  const v = shownId !== null && shownId !== of.current.id && older.data ? older.data : of.current;
  const onChanged = (next: OfferOf) => {
    setShownId(null);
    changed(next);
  };
  const editable = v.editable && rights.edit;
  const [dialog, setDialog] = useState<null | 'send' | 'accept' | 'events' | 'pdf'>(null);
  // owner 2026-10-31: the PDF of any version; not with an event the user may not see (the offer shows no amounts then)
  const pdf = rights.pdf && v.hiddenEvents === 0 && v.totals !== undefined;
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<OfferVersion>(`/api/quotes/offer-versions/${v.id}`, { method: 'PATCH', body: { ...body, version: v.version } }),
    onSuccess: (next) => onChanged({ ...of, current: next }),
  });
  const rename = useMutation({
    mutationFn: (name: string) => api<OfferOf['offer']>(`/api/quotes/offers/${of.offer.id}`, { method: 'PATCH', body: { name, version: of.offer.version } }),
    onSuccess: (offer) => onChanged({ ...of, offer }),
  });
  const fresh = useMutation({ mutationFn: () => api<OfferOf>(`/api/quotes/offers/${of.offer.id}/versions`, { method: 'POST', body: { fromVersionId: v.id } }), onSuccess: (next) => onChanged(next) });
  const failed = [patch.error, rename.error, fresh.error].find((e) => e) ?? null;
  const stale = failed instanceof ApiError && (failed.status === 409 || failed.code === 'version_frozen');
  const title = of.offer.name || `Oferta łączona dla: ${of.offer.clientName}`;

  return (
    <article className="offer-sheet" aria-label={`Oferta łączona: ${title}`}>
      <header className="offer-head">
        <div className="offer-title">
          {editable ? <NameField value={of.offer.name} placeholder={`Oferta łączona dla: ${of.offer.clientName}`} onSave={(name) => rename.mutate(name)} /> : <h1>{title}</h1>}
          <p className="offer-client">
            Klient: <strong>{of.offer.clientName}</strong>
            <span className="offer-version" data-state={v.state}>
              v{v.number} {OFFER_STATE_LABELS[v.state]}
            </span>
          </p>
          {of.versions.length > 1 && (
            <div className="segment small offer-versions" role="group" aria-label="Wersje oferty">
              {of.versions.map((x) => (
                <button key={x.id} type="button" aria-pressed={x.id === v.id} onClick={() => setShownId(x.id === of.current.id ? null : x.id)}>
                  v{x.number} {OFFER_STATE_LABELS[x.state]}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="offer-actions">
          {pdf && (
            <>
              <button className="btn" type="button" onClick={() => setDialog('pdf')} data-feature-id="quotes.offers">
                Podgląd PDF
              </button>
              <button className="btn" type="button" onClick={() => downloadFrom(offerPdfUrl(v.id))}>
                Pobierz PDF
              </button>
            </>
          )}
          {v.state === 'robocza' && rights.send && v.editable && (
            <button className="btn primary" type="button" onClick={() => setDialog('send')}>
              Oznacz jako wysłaną…
            </button>
          )}
          {v.state === 'wyslana' && rights.decide && v.hiddenEvents === 0 && (
            <button className="btn primary" type="button" onClick={() => setDialog('accept')}>
              Zaakceptowana…
            </button>
          )}
          {v.state !== 'robocza' && rights.edit && !of.versions.some((x) => x.state === 'robocza') && (
            <button className="btn" type="button" disabled={fresh.isPending} onClick={() => fresh.mutate()}>
              Nowa wersja
            </button>
          )}
        </div>
      </header>

      {stale && <ConflictNotice onReload={onReload} />}
      {failed && !stale && (
        <p className="alert" role="alert">
          {failed instanceof ApiError ? (fieldIssues(failed)[0]?.message ?? failed.message) : 'Nie udało się zapisać'}
        </p>
      )}
      {v.superseded && <p className="alert q-notice">Nieaktualna — kosztorys zaakceptowany w innej wersji</p>}
      {v.state !== 'robocza' && (
        <p className="hint offer-frozen">
          {v.state === 'wyslana' ? `Wysłana${v.sentTo ? ` do: ${v.sentTo}` : ''}. Pokazuje to, co dostał klient` : 'Zaakceptowana przez klienta. Pokazuje to, co klient zaakceptował'} — zmiany w kosztorysach jej nie zmieniają. Żeby coś poprawić, utwórz nową wersję.
        </p>
      )}
      {v.hiddenEvents > 0 && <p className="hint">Oferta ma też {v.hiddenEvents === 1 ? 'wydarzenie, którego' : `${v.hiddenEvents} wydarzenia, których`} nie widzisz — dlatego bez sum i bez zmian.</p>}
      {v.vatMismatch && <p className="alert q-notice">Któryś kosztorys ma inną stawkę VAT niż oferta ({formatDecimal(v.vatBp / 100)}%). Oferta liczy VAT swoją stawką.</p>}

      <div className="offer-toolbar">
        <div className="segment" role="group" aria-label="Szczegóły w ofercie">
          {DETAILS.map((d) => (
            <button key={d} type="button" aria-pressed={v.detailLevel === d} disabled={!editable && v.detailLevel !== d} onClick={() => editable && d !== v.detailLevel && patch.mutate({ detailLevel: d })}>
              {DETAIL_LABELS[d]}
            </button>
          ))}
        </div>
        {editable && (
          <button className="btn" type="button" onClick={() => setDialog('events')}>
            Dodaj wydarzenie…
          </button>
        )}
      </div>

      <ol className="offer-events" aria-label="Wydarzenia w ofercie">
        {v.events.map((e) => (
          <EventBlock
            key={e.eventId}
            e={e}
            detail={v.detailLevel}
            seesEvents={rights.seesEvents}
            onRemove={editable && v.events.length > 1 ? () => patch.mutate({ eventIds: v.events.filter((x) => x.eventId !== e.eventId).map((x) => x.eventId) }) : undefined}
          />
        ))}
      </ol>

      {v.totals && <TotalsBlock v={v} editable={editable} onPatch={(body) => patch.mutate(body)} />}

      <div className="offer-notes">
        <h2>Uwagi do oferty</h2>
        {editable ? <NotesField value={v.notes} onSave={(notes) => patch.mutate({ notes })} /> : <p>{v.notes || <span className="muted">Brak uwag.</span>}</p>}
      </div>

      {dialog === 'send' && (
        <SendDialog
          of={of}
          v={v}
          onDone={(next) => {
            setDialog(null);
            onChanged(next);
          }}
          onClose={() => setDialog(null)}
          onReload={onReload}
        />
      )}
      {dialog === 'accept' && (
        <AcceptDialog
          of={of}
          v={v}
          onDone={(next) => {
            setDialog(null);
            onChanged(next);
          }}
          onClose={() => setDialog(null)}
          onReload={onReload}
        />
      )}
      {dialog === 'pdf' && <PdfPreviewDialog offer versionId={v.id} number={v.number} onDownload={() => downloadFrom(offerPdfUrl(v.id))} onClose={() => setDialog(null)} />}
      {dialog === 'events' && (
        <OfferEventsDialog
          clientId={of.offer.clientId}
          chosen={v.events.map((e) => e.eventId)}
          onSave={(eventIds) => {
            setDialog(null);
            patch.mutate({ eventIds });
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </article>
  );
}

function eventDates(e: OfferEvent): string {
  if (e.segments) return formatSegments(e.segments);
  return e.startsAt && e.endsAt ? formatEventRange(e.startsAt, e.endsAt) : 'bez terminu';
}

const money = (g: number | undefined) => (g === undefined ? '' : formatPln(g));
const days = (milli: number | undefined) => (milli === undefined ? '' : formatDecimal(milli / 1000));

function EventBlock({ e, detail, seesEvents, onRemove }: { e: OfferEvent; detail: DetailLevel; seesEvents: boolean; onRemove: (() => void) | undefined }) {
  const stamp = e.startsAt ? dateStamp(e.startsAt) : null;
  const place = [e.location, e.locationAddress].filter((s) => s && s.trim() !== '').join(', ');
  const sections = e.sections.map((s) => ({ ...s, lines: e.hideZeroLines ? s.lines.filter((l) => l.netGrosze !== 0) : s.lines })).filter((s) => s.lines.length > 0);
  return (
    <li className="offer-event" data-testid="offer-event">
      <div className="offer-stamp" aria-hidden="true">
        {stamp ? (
          <>
            <span className="offer-stamp-day">{stamp.day}</span>
            <span className="offer-stamp-month">{stamp.month}</span>
          </>
        ) : (
          <span className="offer-stamp-month">—</span>
        )}
      </div>
      <div className="offer-event-body">
        <div className="offer-event-head">
          <h2>
            {e.deleted || !seesEvents ? <span>{e.name}</span> : <Link to={quoteFromListUrl(e.eventId)}>{e.name}</Link>}
          </h2>
          <span className="offer-event-total">{money(e.totalGrosze)}</span>
        </div>
        <p className="offer-event-meta">
          {eventDates(e)}
          {place && <> — {place}</>}
          {e.versionNumber !== null && <span className="muted">, kosztorys v{e.versionNumber}</span>}
        </p>
        {e.clientMismatch && <p className="alert">To wydarzenie ma teraz innego klienta niż oferta. Usuń je z oferty albo popraw klienta w wydarzeniu.</p>}
        {!e.quoteId && <p className="hint">To wydarzenie nie ma jeszcze kosztorysu — przed wysłaniem oferty go przygotuj.</p>}
        {detail === 'lines' && sections.length > 0 && (
          <table className="table offer-lines" aria-label={`Pozycje: ${e.name}`}>
            <thead>
              <tr>
                <th scope="col">Pozycja</th>
                <th scope="col" className="num">
                  Ilość
                </th>
                <th scope="col" className="num">
                  Dni
                </th>
                <th scope="col" className="num">
                  Cena
                </th>
                <th scope="col" className="num">
                  Netto
                </th>
              </tr>
            </thead>
            {sections.map((s) => (
              <tbody key={s.id}>
                <tr className="offer-section-row">
                  <th scope="rowgroup" colSpan={5}>
                    {s.name}
                  </th>
                </tr>
                {s.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.name}</td>
                    <td className="num">{l.quantity === undefined ? '' : formatDecimal(l.quantity)}</td>
                    <td className="num">{days(l.daysMilli)}</td>
                    <td className="num">{money(l.unitPriceGrosze)}</td>
                    <td className="num">{money(l.netGrosze)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        )}
        {detail === 'sections' && sections.length > 0 && (
          <ul className="offer-section-sums" aria-label={`Sekcje: ${e.name}`}>
            {sections.map((s) => (
              <li key={s.id}>
                <span>{s.name}</span>
                <span className="num">{money(s.netGrosze)}</span>
              </li>
            ))}
          </ul>
        )}
        {onRemove && (
          <button className="btn small ghost offer-remove" type="button" aria-label={`Usuń z oferty: ${e.name}`} onClick={onRemove}>
            Usuń z oferty
          </button>
        )}
      </div>
    </li>
  );
}

/** The end of the offer in its fixed order; both discounts and the net are typed here (owner: money is always editable). */
function TotalsBlock({ v, editable, onPatch }: { v: OfferVersion; editable: boolean; onPatch: (body: Record<string, unknown>) => void }) {
  const t = v.totals!;
  const rows = totalsRows(t, v.vatBp);
  const editor = (key: string): ReactNode => {
    if (!editable) return null;
    if (key === 'equipmentDiscount' && v.equipmentDiscount) return <DiscountField label="Rabat na sprzęt" value={v.equipmentDiscount} capped={t.equipmentDiscountCapped} onSave={(d) => onPatch({ equipmentDiscount: d })} />;
    if (key === 'peopleDiscount' && v.peopleDiscount) return <DiscountField label="Rabat na ludzi" value={v.peopleDiscount} capped={t.peopleDiscountCapped} onSave={(d) => onPatch({ peopleDiscount: d })} />;
    if (key === 'net' && v.manualNetGrosze !== undefined) return <ManualNetField value={v.manualNetGrosze} computed={t.computedNetGrosze} onSave={(m) => onPatch({ manualNetGrosze: m })} />;
    if (key === 'vat') return <VatField value={v.vatBp} onSave={(vatBp) => onPatch({ vatBp })} />;
    return null;
  };
  const previous = v.previousManualNetGrosze;
  return (
    <section className="offer-totals" aria-label="Podsumowanie oferty" data-testid="offer-totals">
      <dl>
        {rows.map((r) => (
          <div key={r.key} className={`offer-total-row row-${r.key}`} data-row={r.key}>
            <dt>
              {r.label}
              {r.key === 'equipmentDiscount' ? discountNote(v.equipmentDiscount) : r.key === 'peopleDiscount' ? discountNote(v.peopleDiscount) : ''}
              {r.key === 'net' && t.manual && <span className="muted"> (wpisana ręcznie)</span>}
            </dt>
            <dd className="offer-total-edit">{editor(r.key)}</dd>
            <dd className="num">{formatPln(r.grosze)}</dd>
          </div>
        ))}
      </dl>
      {previous != null && v.manualNetGrosze === null && <p className="hint">W poprzedniej wersji wpisano ręcznie {formatPln(previous)} netto — nowa wersja liczy z kosztorysów. Wpisz kwotę jeszcze raz, jeśli nadal obowiązuje.</p>}
    </section>
  );
}

function DiscountField({ label, value, capped, onSave }: { label: string; value: OfferDiscount; capped: boolean; onSave: (d: OfferDiscount) => void }) {
  const [kind, setKind] = useState(value.kind);
  const [text, setText] = useState(value.value === 0 ? '' : discountToInput(value));
  const [error, setError] = useState('');
  useEffect(() => {
    setKind(value.kind);
    setText(value.value === 0 ? '' : discountToInput(value));
  }, [value.kind, value.value]);
  function save(k = kind, raw = text) {
    try {
      const next = { kind: k, value: discountFromInput(k, raw) };
      setError('');
      if (next.kind !== value.kind || next.value !== value.value) onSave(next);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <span className="offer-discount">
      <span className="segment small" role="group" aria-label={`${label}: procent albo kwota`}>
        {(['pct', 'amount'] as const).map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={kind === k}
            onClick={() => {
              setKind(k);
              setText('');
              save(k, '');
            }}
          >
            {k === 'pct' ? '%' : 'zł'}
          </button>
        ))}
      </span>
      <input
        aria-label={label}
        inputMode="decimal"
        value={text}
        placeholder="0"
        onChange={(e) => setText(e.target.value.replace(/\./g, ','))}
        onBlur={() => save()}
        onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
      />
      {capped && <span className="hint">więcej niż suma — liczony do sumy</span>}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function ManualNetField({ value, computed, onSave }: { value: number | null; computed: number; onSave: (m: number | null) => void }) {
  const [text, setText] = useState(value === null ? '' : plnToInput(value));
  const [error, setError] = useState('');
  useEffect(() => setText(value === null ? '' : plnToInput(value)), [value]);
  function save() {
    if (text.trim() === '') {
      setError('');
      if (value !== null) onSave(null);
      return;
    }
    try {
      const m = parsePln(text);
      setError('');
      if (m !== value) onSave(m);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <span className="offer-manual">
      {value !== null && value !== computed && <span className="hint offer-computed">wyliczone: {formatPln(computed)}</span>}
      <input aria-label="Razem netto wpisane ręcznie" inputMode="decimal" value={text} placeholder={`wyliczone: ${formatPln(computed)}`} onChange={(e) => setText(e.target.value.replace(/\./g, ','))} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()} />
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function VatField({ value, onSave }: { value: number; onSave: (bp: number) => void }) {
  const [text, setText] = useState(formatDecimal(value / 100));
  useEffect(() => setText(formatDecimal(value / 100)), [value]);
  const save = () => {
    const bp = parseScaled(text.replace(/\s*%\s*$/, ''), 2, { maxIntegerDigits: 3 });
    if (bp === null || bp > 10000) return setText(formatDecimal(value / 100));
    if (bp !== value) onSave(bp);
  };
  return <input className="offer-vat" aria-label="Stawka VAT oferty (%)" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value.replace(/\./g, ','))} onBlur={save} onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()} />;
}

function NameField({ value, placeholder, onSave }: { value: string; placeholder: string; onSave: (name: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <input
      className="offer-name"
      aria-label="Nazwa oferty"
      value={text}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => text.trim() !== value && onSave(text.trim())}
      onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLInputElement).blur()}
    />
  );
}

function NotesField({ value, onSave }: { value: string; onSave: (notes: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return <NotesInput aria-label="Uwagi do oferty" value={text} onChange={(e) => setText(e.target.value)} onBlur={() => text.trim() !== value && onSave(text)} placeholder="np. cena przy zamówieniu całego pakietu" />;
}

function SendDialog({ of, v, onDone, onClose, onReload }: { of: OfferOf; v: OfferVersion; onDone: (of: OfferOf) => void; onClose: () => void; onReload: () => void }) {
  const [sentTo, setSentTo] = useState(of.offer.clientName);
  const [note, setNote] = useState('');
  const send = useMutation({ mutationFn: () => api<OfferOf>(`/api/quotes/offer-versions/${v.id}/mark-sent`, { method: 'POST', body: { sentTo, note, version: v.version } }), onSuccess: onDone });
  // review I2: a manual net may be stale over the live quotes
  const manual = v.totals?.manual && v.totals.netGrosze !== v.totals.computedNetGrosze ? v.totals : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (sentTo.trim() !== '') send.mutate();
  };
  const error = send.error instanceof ApiError ? send.error : null;
  const stale = !!error && error.status === 409;
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="offer-send-title" onSubmit={submit}>
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="offer-send-title">Oznacz ofertę jako wysłaną</h2>
        <p className="muted">Oferta zostanie zamrożona. Robocze wersje kosztorysów jej wydarzeń też zostaną oznaczone jako wysłane — tak jak przy wysyłce kosztorysu.</p>
        {manual && (
          <p className="alert" role="alert">
            Razem netto jest wpisane ręcznie ({formatPln(manual.netGrosze)}), a z kosztorysów wychodzi {formatPln(manual.computedNetGrosze)}. Sprawdź kwotę przed wysłaniem.
          </p>
        )}
        {stale && <ConflictNotice onReload={onReload} />}
        <label className="field">
          <span>Do kogo</span>
          <input value={sentTo} onChange={(e) => setSentTo(e.target.value)} autoComplete="off" autoFocus required />
        </label>
        <label className="field">
          <span>Notatka (nieobowiązkowo)</span>
          <NotesInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. wysłane mailem" />
        </label>
        {error && !stale && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={send.isPending || sentTo.trim() === ''}>
            Oznacz jako wysłaną
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}

type Elsewhere = { eventId: string; name: string; quoteId: string; acceptedNumber: number };

function AcceptDialog({ of, v, onDone, onClose, onReload }: { of: OfferOf; v: OfferVersion; onDone: (of: OfferOf) => void; onClose: () => void; onReload: () => void }) {
  const [note, setNote] = useState('');
  // review I4: per quote — the ones whose other accepted version the user agrees to replace
  const [replace, setReplace] = useState<string[]>([]);
  const accept = useMutation({ mutationFn: () => api<OfferOf>(`/api/quotes/offer-versions/${v.id}/accept`, { method: 'POST', body: { note, version: v.version, ...(replace.length > 0 ? { replaceQuoteIds: replace } : {}) } }), onSuccess: onDone });
  const error = accept.error instanceof ApiError ? accept.error : null;
  const [elsewhere, setElsewhere] = useState<Elsewhere[]>([]);
  useEffect(() => {
    if (error?.code === 'accepted_elsewhere') setElsewhere(((error.current as { events?: Elsewhere[] } | undefined)?.events ?? []) as Elsewhere[]);
  }, [error]);
  const stale = !!error && error.status === 409 && error.code !== 'accepted_elsewhere';
  const pending = elsewhere.filter((e) => !replace.includes(e.quoteId));
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offer-accept-title"
        onSubmit={(e) => {
          e.preventDefault();
          accept.mutate();
        }}
      >
        <DialogLayer onClose={onClose} busy={accept.isPending} />
        <h2 id="offer-accept-title">Klient zaakceptował ofertę (v{v.number})</h2>
        <p className="muted">Każdy kosztorys z oferty ({v.events.length}) zostanie oznaczony jako zaakceptowany — wszystkie naraz albo żaden. Gdy oferta ma rabaty, każdy kosztorys dostanie nową wersję z liniami „Rabat pakietowy”, tak żeby razem dawały kwotę oferty.</p>
        {stale && <ConflictNotice onReload={onReload} />}
        <label className="field">
          <span>Notatka (nieobowiązkowo)</span>
          <NotesInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="np. potwierdzone telefonicznie" />
        </label>
        {elsewhere.length > 0 && (
          <fieldset className="offer-picker-list">
            <legend>Te kosztorysy mają już zaakceptowaną inną wersję — zaakceptować mimo to?</legend>
            {elsewhere.map((e) => (
              <label key={e.quoteId} className="check">
                <input type="checkbox" checked={replace.includes(e.quoteId)} onChange={(ev) => setReplace((r) => (ev.target.checked ? [...r, e.quoteId] : r.filter((x) => x !== e.quoteId)))} />
                <span>
                  Zaakceptuj mimo to: {e.name} (zaakceptowana teraz v{e.acceptedNumber})
                </span>
              </label>
            ))}
            <p className="hint">Tamta akceptacja przestanie obowiązywać, a oferta, która na niej stała, będzie oznaczona jako nieaktualna.</p>
          </fieldset>
        )}
        {error && !stale && error.code !== 'accepted_elsewhere' && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={accept.isPending || (elsewhere.length > 0 && pending.length > 0)}>
            Zaakceptowana
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
